import * as React from 'react';
import { get } from 'lodash';

import { getSystemMessageText } from 'utils/messenger';
import { getDisplayDateAndTime } from 'utils/date';

import SubcampaignOfferActions from 'modules/subcampaigns/components/SubcampaignOfferActions';

import CcmSystemChatMessageFirstContentReminder from '../system/CcmSystemChatMessageFirstContentReminder';
import CcmSystemChatMessageLastContentReminder from '../system/CcmSystemChatMessageLastContentReminder';
import SystemChatMessageQuoteRequested from '../system/SystemChatMessageQuoteRequested';
import SystemChatMessageInvite from '../system/SystemChatMessageInvite';
import SystemChatMessageBrandRejected from '../system/SystemChatMessageBrandRejected';
import SystemChatMessageBriefUpdated from '../system/SystemChatMessageBriefUpdated';
import SystemChatMessageContentMoved from '../system/SystemChatMessageContentMoved';
import SystemChatMessagePostAdded from '../system/SystemChatMessagePostAdded';
import SystemChatMessageOfferAccepted from '../system/SystemChatMessageOfferAccepted';
import SystemChatMessageOfferDeclined from '../system/SystemChatMessageOfferDeclined';
import SystemChatMessageOfferExpired from '../system/SystemChatMessageOfferExpired';
import SystemChatMessageSubcampaignCancelled from '../system/SystemChatMessageSubcampaignCancelled';
import SystemChatMessageNegotiationsReopened from '../system/SystemChatMessageNegotiationsReopened';
import SystemChatMessageShippingAddressSet from '../system/SystemChatMessageShippingAddressSet';
import SystemChatMessagePostAttachmentAdded from '../system/SystemChatMessagePostAttachmentAdded';
import SystemChatMessageLinksUpdated from '../system/SystemChatMessageLinksUpdated';
import SystemChatMessageDeliverableDetailsUpdated from '../system/SystemChatMessageDeliverableDetailsUpdated';
import SystemChatMessageContentForApprovalAdded from '../system/SystemChatMessageContentForApprovalAdded';
import SystemChatMessageContentForApprovalReviewed from '../system/SystemChatMessageContentForApprovalReviewed';
import SystemChatMessageContentForApprovalAddedV2 from '../system/SystemChatMessageContentForApprovalAddedV2';
import SystemChatMessageContentForApprovalReviewedV2 from '../system/SystemChatMessageContentForApprovalReviewedV2';
import SystemChatMessageContentForApprovalRemovedV2 from '../system/SystemChatMessageContentForApprovalRemovedV2';
import SystemChatMessageContentForApprovalResetV2 from '../system/SystemChatMessageContentForApprovalResetV2';
import SystemChatMessageContentForApprovalPostAddedV2 from '../system/SystemChatMessageContentForApprovalPostAddedV2';
import SystemChatMessageContentForApprovalNoteUpdatedV2 from '../system/SystemChatMessageContentForApprovalNoteUpdatedV2';
import SystemChatMessageContentForApprovalInsightsAddedV2 from '../system/SystemChatMessageContentForApprovalInsightsAddedV2';
import SystemChatMessageContentForApprovalInsightsRemovedV2 from '../system/SystemChatMessageContentForApprovalInsightsRemovedV2';
import SystemChatMessageVariablePaymentsUpdated from '../system/SystemChatMessageVariablePaymentsUpdated';
import SystemChatMessageQuoteProvided from '../system/SystemChatMessageQuoteProvided';
import SystemChatMessageBankBusinessDetailsUpdate from '../system/SystemChatMessageBankBusinessDetailsUpdate';
import SystemChatMessageShipmentAdded from '../system/SystemChatMessageShipmentAdded';

import type {
  Campaign,
  MessengerMessage,
  Subcampaign,
  SubcampaignInfluencerProfile
} from 'types';

// https://github.bus.zalan.do/Team-Collab/collab-backend/blob/master/docs/offer-flow.md

type Props = {
  message: MessengerMessage,
  campaign: Campaign,
  influencerProfile: SubcampaignInfluencerProfile,
  subcampaign: Subcampaign,
  subcampaignId: number,
  isBrand: boolean,
  latestMessageExternalIdForInfluencerAction: string | undefined | null
};

class SystemMessageOld extends React.PureComponent<Props> {
  static defaultProps = {
    isBrand: false
  };

  getTaskTypeOtherType(): string {
    const { message } = this.props;
    const metadata = message.metadata;

    return metadata.type.replace(/insights/ig, 'files');
  }

  renderSubcampaignInfluencerActions(): React.ReactNode {
    const { message, subcampaign, latestMessageExternalIdForInfluencerAction } = this.props;

    if (message.externalId !== latestMessageExternalIdForInfluencerAction) {
      return null;
    }

    return (
      <SubcampaignOfferActions
        buttonWidth="normal"
        subcampaign={subcampaign}
        fromMessenger
      />
    );
  }

  render() {
    const {
      message,
      campaign,
      influencerProfile,
      subcampaign,
      subcampaignId,
      isBrand
    } = this.props;

    const type = get(message, 'metadata.type', '').toLowerCase();

    if (!type) {
      return null;
    }

    // use "files" when it's task type "other"
    if (message.metadata.postType === 'other') {
      message.metadata.type = this.getTaskTypeOtherType();
    }

    const text = getSystemMessageText(message, campaign, influencerProfile, subcampaign, isBrand);
    const time = getDisplayDateAndTime(message.createdAt);

    const influencerActions = this.renderSubcampaignInfluencerActions();

    let systemMessageComponent;

    if (type === 'brand_rejected') {
      systemMessageComponent = (
        <SystemChatMessageBrandRejected
          text={text}
          time={time}
        />
      );
    }

    if (type === 'offer_expired') {
      systemMessageComponent = (
        <SystemChatMessageOfferExpired
          text={text}
          time={time}
          isBrand={isBrand}
        />
      );
    }

    if (type === 'set_shipping_address') {
      systemMessageComponent = (
        <SystemChatMessageShippingAddressSet
          metadata={message.metadata}
          text={text}
          time={time}
        />
      );
    }

    if (type === 'brief_update') {
      systemMessageComponent = (
        <SystemChatMessageBriefUpdated
          text={text}
          time={time}
          campaignId={campaign.id}
          subcampaignId={subcampaignId}
          isBrand={isBrand}
        />
      );
    }

    if (type === 'influencer_invite') {
      systemMessageComponent = (
        <SystemChatMessageInvite
          influencerActions={influencerActions}
          text={text}
          time={time}
          campaign={campaign}
          subcampaignId={subcampaignId}
          isBrand={isBrand}
        />
      );
    }

    if (type === 'negotiation_reopen') {
      systemMessageComponent = (
        <SystemChatMessageNegotiationsReopened
          campaign={campaign}
          message={message}
          influencerActions={influencerActions}
          text={text}
          time={time}
          isBrand={isBrand}
        />
      );
    }

    if (type === 'offer_accept') {
      systemMessageComponent = (
        <SystemChatMessageOfferAccepted
          text={text}
          time={time}
        />
      );
    }

    if (type === 'offer_decline') {
      systemMessageComponent = (
        <SystemChatMessageOfferDeclined
          text={text}
          metadata={message.metadata}
          time={time}
          influencerProfile={influencerProfile}
          isBrand={isBrand}
        />
      );
    }

    if (type === 'influencer_post') {
      systemMessageComponent = (
        <SystemChatMessagePostAdded
          metadata={message.metadata}
          text={text}
          time={time}
        />
      );
    }

    if (type === 'influencer_deliverable') {
      systemMessageComponent = (
        <SystemChatMessagePostAttachmentAdded
          metadata={message.metadata}
          text={text}
          time={time}
          campaign={campaign}
          isBrand={isBrand}
        />
      );
    }

    if (type === 'content_for_approval_added') {
      systemMessageComponent = (
        <SystemChatMessageContentForApprovalAdded
          metadata={message.metadata}
          text={text}
          time={time}
          subcampaignId={subcampaignId}
          campaign={campaign}
          isBrand={isBrand}
        />
      );
    }

    if (type === 'content_for_approval_reviewed') {
      systemMessageComponent = (
        <SystemChatMessageContentForApprovalReviewed
          metadata={message.metadata}
          text={text}
          time={time}
          subcampaignId={subcampaignId}
          campaign={campaign}
          isBrand={isBrand}
        />
      );
    }

    if (type === 'content_for_approval_added_v2') {
      systemMessageComponent = (
        <SystemChatMessageContentForApprovalAddedV2
          metadata={message.metadata}
          text={text}
          time={time}
          subcampaignId={subcampaignId}
          campaign={campaign}
          isBrand={isBrand}
        />
      );
    }

    if (type === 'content_for_approval_reviewed_v2') {
      systemMessageComponent = (
        <SystemChatMessageContentForApprovalReviewedV2
          metadata={message.metadata}
          text={text}
          time={time}
          subcampaignId={subcampaignId}
          campaign={campaign}
          isBrand={isBrand}
        />
      );
    }

    if (type === 'content_for_approval_removed_v2') {
      systemMessageComponent = (
        <SystemChatMessageContentForApprovalRemovedV2
          metadata={message.metadata}
          text={text}
          time={time}
          subcampaignId={subcampaignId}
          campaign={campaign}
          isBrand={isBrand}
        />
      );
    }

    if (type === 'content_for_approval_reset_v2') {
      systemMessageComponent = (
        <SystemChatMessageContentForApprovalResetV2
          metadata={message.metadata}
          text={text}
          time={time}
          subcampaignId={subcampaignId}
          campaign={campaign}
          isBrand={isBrand}
        />
      );
    }

    if (type === 'content_for_approval_post_added_v2') {
      systemMessageComponent = (
        <SystemChatMessageContentForApprovalPostAddedV2
          metadata={message.metadata}
          text={text}
          time={time}
          subcampaignId={subcampaignId}
          campaign={campaign}
          isBrand={isBrand}
        />
      );
    }

    if (
      type === 'content_for_approval_note_added_v2' ||
      type === 'content_for_approval_note_edited_v2' ||
      type === 'content_for_approval_note_deleted_v2'
    ) {
      systemMessageComponent = (
        <SystemChatMessageContentForApprovalNoteUpdatedV2
          metadata={message.metadata}
          text={text}
          time={time}
          subcampaignId={subcampaignId}
          campaign={campaign}
          isBrand={isBrand}
        />
      );
    }

    if (type === 'content_for_approval_insights_added_v2') {
      systemMessageComponent = (
        <SystemChatMessageContentForApprovalInsightsAddedV2
          metadata={message.metadata}
          text={text}
          time={time}
          subcampaignId={subcampaignId}
          campaign={campaign}
          isBrand={isBrand}
        />
      );
    }

    if (type === 'content_for_approval_insights_removed_v2') {
      systemMessageComponent = (
        <SystemChatMessageContentForApprovalInsightsRemovedV2
          metadata={message.metadata}
          text={text}
          time={time}
          subcampaignId={subcampaignId}
          campaign={campaign}
          isBrand={isBrand}
        />
      );
    }

    if (type === 'content_for_approval_moved') {
      systemMessageComponent = (
        <SystemChatMessageContentMoved
          metadata={message.metadata}
          text={text}
          time={time}
          subcampaignId={subcampaignId}
          campaignId={campaign.id}
          isBrand={isBrand}
        />
      );
    }

    if (type === 'influencer_quote') {
      systemMessageComponent = (
        <SystemChatMessageQuoteRequested
          influencerActions={influencerActions}
          text={text}
          time={time}
          campaign={campaign}
          subcampaignId={subcampaignId}
          isBrand={isBrand}
        />
      );
    }

    if (type === 'subcampaign_cancel') {
      systemMessageComponent = (
        <SystemChatMessageSubcampaignCancelled
          text={text}
          time={time}
          metadata={message.metadata}
          isBrand={isBrand}
        />
      );
    }

    if (type === 'deliverable_links_updated') {
      systemMessageComponent = (
        <SystemChatMessageLinksUpdated
          campaign={campaign}
          subcampaignId={subcampaignId}
          time={time}
          metadata={message.metadata}
          isBrand={isBrand}
        />
      );
    }

    if (type === 'deliverable_details_updated') {
      systemMessageComponent = (
        <SystemChatMessageDeliverableDetailsUpdated
          text={text}
          time={time}
          campaignId={campaign.id}
          subcampaignId={subcampaignId}
          deliverableId={get(message.metadata, 'payload.deliverable.id', null)}
          isBrand={isBrand}
        />
      );
    }

    if (type === 'variable_payments_updated') {
      systemMessageComponent = (
        <SystemChatMessageVariablePaymentsUpdated
          text={text}
          time={time}
          campaignId={campaign.id}
          subcampaignId={subcampaignId}
          isBrand={isBrand}
        />
      );
    }

    if (type === 'offer_quote') {
      systemMessageComponent = (
        <SystemChatMessageQuoteProvided
          text={text}
          time={time}
          campaignId={campaign.id}
          subcampaignId={subcampaignId}
          isBrand={isBrand}
        />
      );
    }

    if (type === 'subcampaign_business_details_updated' ||
      type === 'subcampaign_bank_details_updated' ||
      type === 'subcampaign_no_business_details_provided') {
      systemMessageComponent = (
        <SystemChatMessageBankBusinessDetailsUpdate
          time={time}
          type={type}
        />
      );
    }

    if (type === 'offer_product_shipment_added') {
      systemMessageComponent = (
        <SystemChatMessageShipmentAdded
          text={text}
          time={time}
        />
      );
    }

    if (type === 'ccm_first_content_reminder') {
      systemMessageComponent = (
        <CcmSystemChatMessageFirstContentReminder
          time={time}
          metadata={message.metadata}
        />
      );
    }

    if (type === 'ccm_last_content_reminder') {
      systemMessageComponent = (
        <CcmSystemChatMessageLastContentReminder
          time={time}
          metadata={message.metadata}
        />
      );
    }

    return (
      <div className="messages-system-message" data-id={message.externalId} data-hj-suppress>
        {systemMessageComponent}
      </div>
    );
  }
}

export default SystemMessageOld;
